import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { Box, Slider, Tooltip } from '@mui/material';
import { Plus } from 'lucide-react';
import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import OverflowScrollRow from '~/components/OverflowScrollRow';
import { BlendSettings, pairHasPet } from '~/domain/viewport/fusionBlend';
import {
    BlendCandidate,
    BlendSource,
    DatasetMetadata,
} from '../adapters/types';
import {
    useChangeVersion,
    useSourceSnapshot,
} from '../adapters/useChangeVersion';
import {
    CONSOLE_CONTROL_GAP,
    consoleDividerSx,
    consoleGroupSx,
    consoleSliderSx,
    CONSOLE_DISABLED_SX,
    FOOTER_CORNER_BUTTON_SX,
    FOOTER_CORNER_SX,
    ROW_FOOTER_SX,
} from '../chrome';
import CloseRowButton from '../CloseRowButton';
import {
    FusionColorMenu,
    PetColorMapMenu,
    TileSizeMenu,
} from './blendChoiceMenus';
import { printedBlendShare } from './blendShare';
import ImageWindowControls from './ImageWindowControls';
import PairControl, { PairView } from './PairControl';
import { pairViewOf, pairViewsFor, pairViewTransition } from './pairView';
import RowImageMenu from './RowImageMenu';
import RowLayoutMenu, { RowLayoutChoice } from './RowLayoutMenu';
import { RowWindowingControls } from './tileLink';
import { pairEndLabels } from '../pairEndLabels';
import { offersWindowPresets, seriesNumberText } from './tileViewModel';

interface RowFooterProps {
    /** The shape the row hangs, and the list that changes it. */
    readonly layout: RowLayoutChoice;
    /** The image the row draws, and the list that changes it. */
    readonly rowImage: DatasetMetadata;
    readonly availableDatasets: ReadonlyArray<DatasetMetadata>;
    readonly onBindRowImage: (seriesInstanceUid: SeriesInstanceUid) => void;
    /** The controls of what is drawn over the row's image, before its shape. */
    readonly overlayControls?: ReactNode;
    /**
     * Window/level for the row's own image. Only a named preset reaches
     * further, to the series the link policy names.
     */
    readonly rowWindowing: RowWindowingControls;
    /** Window/level for an image this row fused, which reaches that series alone. */
    readonly fusedWindowingFor: (
        seriesInstanceUid: SeriesInstanceUid
    ) => RowWindowingControls;
    /** Per-row secondary-image blend truth. */
    readonly blendSource: BlendSource;
    readonly onFuse: (candidate: BlendCandidate) => void;
    readonly onClear: () => void;
    readonly onSetBlend: (patch: Partial<BlendSettings>) => void;
    /** Whether the row shows its fused pair apart, one image per pane. */
    readonly sideBySide: boolean;
    readonly onToggleSideBySide: () => void;
    /** Opens an unbound row directly below this one. */
    readonly onAddRowBelow: () => void;
    readonly onCloseRow: () => void;
    /** Whether closing this row is offered; false for the last row left. */
    readonly canClose: boolean;
    /**
     * Whether this is the primary row: the one that shows the primary
     * image. It cannot be closed.
     */
    readonly isPrimary: boolean;
}

interface MixSliderProps {
    readonly rowImage: DatasetMetadata;
    readonly fused: BlendCandidate;
    readonly source: BlendSource;
    readonly onSetBlend: (patch: Partial<BlendSettings>) => void;
}

/**
 * How much of each image the blend shows. The row image lies left, the
 * fused image right, and the thumb sits towards the image that shows more.
 * One share is printed, at the end of the image it belongs to (see
 * {@link printedBlendShare}), so the number can belong to only one of
 * them. The images at both ends, and whose share the number is, are named
 * in the tooltip and the accessible name. The source holds the row image's
 * share; the two are complements.
 */
function MixSlider({ rowImage, fused, source, onSetBlend }: MixSliderProps) {
    const intl = useIntl();
    useChangeVersion(source);
    const alpha = source.getBlend().alpha;
    const fusedShare = 1 - alpha;
    const nameSeries = (seriesNumber: number) =>
        seriesNumberText(intl, seriesNumber);
    const printed = printedBlendShare(rowImage, fused, alpha, nameSeries);
    const percent = `${Math.round(printed.share * 100)}%`;
    const [rowEnd, fusedEnd] = pairEndLabels(rowImage, fused, nameSeries);
    const shareName = intl.formatMessage(
        { id: 'fusion.blendShare' },
        { image: printed.label }
    );
    const share = (
        <Box
            component="span"
            data-testid="blend-share"
            sx={{ display: 'inline-block', minWidth: '4ch', textAlign: 'end' }}
        >
            {percent}
        </Box>
    );

    return (
        <Tooltip
            title={
                <Box
                    sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}
                >
                    <Box>
                        {rowEnd} ← → {fusedEnd}
                    </Box>
                    <Box>
                        {shareName}: {percent}
                    </Box>
                </Box>
            }
            placement="top"
        >
            {/* The mix has no frame, and the thumb at either end of the rail
                reaches past it: the margin keeps both clear of the framed
                controls beside it. */}
            <Box
                data-testid="row-blend-alpha"
                sx={{ ...consoleGroupSx(1), mx: 1 }}
            >
                {printed.end === 'row' ? share : null}
                <Slider
                    data-testid="blend-alpha-slider"
                    size="small"
                    value={fusedShare}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={(_, value) =>
                        onSetBlend({ alpha: 1 - (value as number) })
                    }
                    aria-label={shareName}
                    aria-valuetext={percent}
                    valueLabelDisplay="off"
                    sx={consoleSliderSx(96)}
                />
                {printed.end === 'fused' ? share : null}
            </Box>
        </Tooltip>
    );
}

/**
 * Everything the pair section shows except the mix, as one primitive: which
 * image is fused and every blend control but the alpha. A moved alpha
 * leaves it unchanged, so the section re-renders on a moved control and not
 * on every frame of a slider drag.
 */
function pairSnapshot(source: BlendSource): string {
    const blend = source.getBlend();
    return [
        source.getCurrent()?.seriesInstanceUid,
        blend.mode,
        blend.checkerboardTileSizeMm,
        blend.colorMappingId,
        blend.petColorMapId,
    ].join('|');
}

interface PairSectionProps {
    readonly rowImage: DatasetMetadata;
    readonly fusedWindowingFor: (
        seriesInstanceUid: SeriesInstanceUid
    ) => RowWindowingControls;
    readonly blendSource: BlendSource;
    readonly onFuse: (candidate: BlendCandidate) => void;
    readonly onClear: () => void;
    readonly onSetBlend: (patch: Partial<BlendSettings>) => void;
    readonly sideBySide: boolean;
    readonly onToggleSideBySide: () => void;
    /** Tells the fused image from the row image, without PHI. */
    readonly fusedPairLabel: string | undefined;
}

/**
 * The row's second image and everything that governs the two together.
 *
 * With nothing fused it is the invitation to fuse, after the ramp a lone
 * PET row is drawn through. The whole section reads
 * the row's pair state, so the row's own image group is never re-rendered by
 * a change that belongs to the pair. The section itself follows which image
 * is fused and how the pair is drawn: the mix slider is the one part that
 * follows every frame of the mix, and it subscribes on its own.
 *
 * The view choice joins two facts held elsewhere, the row's hanging and the
 * pair's blend mode, and a choice writes back to whichever it changes.
 */
function PairSection({
    rowImage,
    fusedWindowingFor,
    blendSource,
    onFuse,
    onClear,
    onSetBlend,
    sideBySide,
    onToggleSideBySide,
    fusedPairLabel,
}: PairSectionProps) {
    useSourceSnapshot(blendSource, () => pairSnapshot(blendSource));
    const fused = blendSource.getCurrent();
    const blend = blendSource.getBlend();
    const pairModalities = fused
        ? [rowImage.modality, fused.modality]
        : undefined;
    const view: PairView = {
        current: pairViewOf(sideBySide, blend.mode),
        choices: pairViewsFor(pairModalities),
        onSelect: (chosen) => {
            const transition = pairViewTransition(
                sideBySide,
                blend.mode,
                chosen
            );
            if (transition.mode !== undefined) {
                onSetBlend({ mode: transition.mode });
            }
            if (transition.toggleSideBySide) {
                onToggleSideBySide();
            }
        },
    };
    const pairControl = (
        <PairControl
            source={blendSource}
            onFuse={onFuse}
            onClear={onClear}
            view={view}
        />
    );
    if (!fused) {
        // A lone PET is drawn through the ramp too, so its choice stays.
        return pairHasPet([rowImage.modality]) ? (
            <Box sx={consoleGroupSx(CONSOLE_CONTROL_GAP)}>
                <PetColorMapMenu
                    petColorMapId={blend.petColorMapId}
                    onSelect={(petColorMapId) => onSetBlend({ petColorMapId })}
                />
                {pairControl}
            </Box>
        ) : (
            pairControl
        );
    }
    const hasPet = pairHasPet([rowImage.modality, fused.modality]);
    return (
        <Box sx={consoleGroupSx(CONSOLE_CONTROL_GAP)}>
            <ImageWindowControls
                picker={pairControl}
                windowing={fusedWindowingFor(fused.seriesInstanceUid)}
                withPresets={offersWindowPresets(fused.modality)}
                imageLabel={fusedPairLabel}
            />
            {/* Nothing is blended while the pair is drawn apart, so neither
                the mix nor the tile has anything to say. */}
            {view.current === 'blend' ? (
                <MixSlider
                    rowImage={rowImage}
                    fused={fused}
                    source={blendSource}
                    onSetBlend={onSetBlend}
                />
            ) : null}
            {view.current === 'checkerboard' ? (
                <TileSizeMenu
                    sizeMm={blend.checkerboardTileSizeMm}
                    onSelect={(checkerboardTileSizeMm) =>
                        onSetBlend({ checkerboardTileSizeMm })
                    }
                />
            ) : null}
            {hasPet ? (
                <PetColorMapMenu
                    petColorMapId={blend.petColorMapId}
                    onSelect={(petColorMapId) => onSetBlend({ petColorMapId })}
                />
            ) : (
                <FusionColorMenu
                    colorMappingId={blend.colorMappingId}
                    onSelect={(colorMappingId) =>
                        onSetBlend({ colorMappingId })
                    }
                />
            )}
        </Box>
    );
}

/**
 * Opens another row below the footer's row: a plus sign in a cell of the
 * footer's corner, named by its accessible name and tooltip. Without a
 * handler the control is shown disabled.
 */
export function AddRowButton({
    onAddRowBelow,
}: {
    readonly onAddRowBelow?: () => void;
}) {
    const intl = useIntl();
    const name = intl.formatMessage({ id: 'rowFooter.addRowBelow' });
    const button = (
        <Box
            component="button"
            type="button"
            aria-label={name}
            data-testid="row-add-below"
            disabled={!onAddRowBelow}
            onClick={onAddRowBelow}
            sx={[FOOTER_CORNER_BUTTON_SX, CONSOLE_DISABLED_SX]}
        >
            <Plus size={14} aria-hidden />
        </Box>
    );
    // A disabled button fires no events, so it carries no tooltip. The
    // footer's buttons sit side by side, so the hint opens upward.
    return onAddRowBelow ? (
        <Tooltip title={name} placement="top">
            {button}
        </Tooltip>
    ) : (
        button
    );
}

/**
 * The row's own place in the stack, in the footer's corner: open another
 * row below it, or close it.
 */
function RowStackActions({
    canClose,
    onAddRowBelow,
    onCloseRow,
    isPrimary,
}: {
    readonly canClose: boolean;
    readonly onAddRowBelow: () => void;
    readonly onCloseRow: () => void;
    readonly isPrimary: boolean;
}) {
    return (
        <Box sx={FOOTER_CORNER_SX}>
            <AddRowButton onAddRowBelow={onAddRowBelow} />
            {isPrimary ? null : (
                <>
                    <Box sx={consoleDividerSx('100%')} />
                    <CloseRowButton
                        canClose={canClose}
                        onClose={onCloseRow}
                        tooltipPlacement="top"
                        sx={FOOTER_CORNER_BUTTON_SX}
                    />
                </>
            )}
        </Box>
    );
}

/**
 * The bar under one dataset row: every image the row displays, and how they
 * are displayed together.
 *
 * Each image is read the same way: the control that names it, then its
 * window, then the list of its named windows. No number in the bar can
 * belong to an image the reader has to guess at. Between them stands the
 * pair control: everything after it exists only while the row has a second
 * image, which is what tells the reader what the row is doing. What is
 * drawn over the row's image, such as a dose, the row's own shape and its
 * place in the stack sit at the far end, apart from the images, and stay
 * put when the bar is narrow: the image controls scroll sideways behind a
 * fade rather than wrap, so the bar never grows and pushes the row's tiles
 * off the screen.
 */
export default function RowFooter({
    layout,
    rowImage,
    availableDatasets,
    onBindRowImage,
    overlayControls,
    rowWindowing,
    fusedWindowingFor,
    blendSource,
    onFuse,
    onClear,
    onSetBlend,
    sideBySide,
    onToggleSideBySide,
    onAddRowBelow,
    onCloseRow,
    canClose,
    isPrimary,
}: RowFooterProps) {
    const intl = useIntl();
    // Each image of a pair is named after its end of the pair.
    useSourceSnapshot(
        blendSource,
        () => blendSource.getCurrent()?.seriesInstanceUid
    );
    const fused = blendSource.getCurrent();
    // Each image's controls are named after their image: by its modality
    // alone, or with a pair shown, by its end of the pair.
    const pairLabels = fused
        ? pairEndLabels(rowImage, fused, (seriesNumber) =>
              seriesNumberText(intl, seriesNumber)
          )
        : undefined;
    return (
        <Box data-testid="row-footer" sx={ROW_FOOTER_SX}>
            <OverflowScrollRow
                data-testid="row-image-controls"
                sx={{ gap: CONSOLE_CONTROL_GAP, alignSelf: 'stretch' }}
            >
                <ImageWindowControls
                    picker={
                        <RowImageMenu
                            current={rowImage}
                            available={availableDatasets}
                            onBind={onBindRowImage}
                        />
                    }
                    windowing={rowWindowing}
                    withPresets={offersWindowPresets(rowImage.modality)}
                    imageLabel={pairLabels?.[0] ?? rowImage.modality}
                />
                <PairSection
                    rowImage={rowImage}
                    fusedWindowingFor={fusedWindowingFor}
                    blendSource={blendSource}
                    onFuse={onFuse}
                    onClear={onClear}
                    onSetBlend={onSetBlend}
                    sideBySide={sideBySide}
                    onToggleSideBySide={onToggleSideBySide}
                    fusedPairLabel={pairLabels?.[1]}
                />
            </OverflowScrollRow>
            {overlayControls}
            <RowLayoutMenu {...layout} />
            <RowStackActions
                canClose={canClose}
                onAddRowBelow={onAddRowBelow}
                onCloseRow={onCloseRow}
                isPrimary={isPrimary}
            />
        </Box>
    );
}
